/**
 * FRONT-895: turn a chat into a project + tasks. Launched from the chat
 * footer's "Create project" button (at the Save-to-Graph slot).
 *
 * FRONT-1141 renamed the user-facing wording from "plan" to "project": the
 * button creates a project (and its task checklist), and calling it a plan
 * made people expect something else. Everything internal — the file name, the
 * `eddy.create-plan-from-chat` flag, the /api/work/plan/* endpoints, the
 * reducer — deliberately keeps the original name: renaming the flag would
 * break the gate that is configured under that key in the flag console, and
 * none of it is read by a user.
 *
 * FRONT-1122 generalized the dialog beyond the chat footer: it now takes a
 * `source: PlanSource` (chat / file / document / cron-run) instead of a bare
 * `sessionId`, and `sourceLabel` instead of `chatTitle`. Three more entry
 * points open the SAME dialog against the SAME endpoints: the inbox reader's
 * non-chat resolve footer (`inbox-chat-modal.tsx`), and a per-row overflow
 * menu on the Files and Chats lists (the Files row's More menu in
 * `features/files/finder/row-actions.tsx`, and the Chats list's own menu). The reading
 * indicator and the "From this …:" line are the only source-aware copy —
 * everything else (heading, task editor, duplicate notice, success toast)
 * is identical regardless of where the dialog was opened from.
 *
 * Opens instantly, fires `POST /api/work/plan/extract` in the background, and shows a small "Eddy is reading this {chat|file|document|briefing}…" indicator (per-kind noun via `describeSourceNoun`, FRONT-1122) with
 * skeleton rows while that call is pending — the editable project row + task
 * list appear the moment it lands (success OR the server's own empty/manual
 * fallback; either way the dialog is fully usable manually). Confirm calls
 * `POST /api/work/plan/commit`; a same-name-collision 409 surfaces an inline
 * "Use existing project instead" notice that flips to existing mode and
 * re-commits, rather than a dead-end error. The chat itself is never touched
 * here — closing this dialog does not resolve/close the chat.
 *
 * FRONT-895 review fix: the backend allows the extract pass up to 120s, and
 * "works fully manually if extract fails" was hollow if reaching that
 * fallback meant staring at a spinner for two minutes. A client-side
 * `EXTRACT_READ_TIMEOUT_MS` (~20s) drops to the empty manual form on its own
 * if nothing has landed by then, and a small "Enter manually" text button in
 * the reading state does the same thing on demand. Either path sets
 * `manualOverride` in the reducer state, which then ignores a late-landing
 * extract result rather than clobbering whatever the user has since typed.
 *
 * All the interactive edit logic (toggle/edit/add/remove a task row, switch
 * project mode, seed from a landed draft) lives in the exported
 * `planEditorReducer` below rather than scattered `useState` calls — this
 * repo's frontend tests have no DOM/click simulation (`renderToStaticMarkup`
 * only, see `new-work-entity-dialog.test.tsx`), so pulling every edit into a
 * plain, dispatch-driven reducer is what makes "exclude a task", "add a
 * task", etc. unit-testable without mounting anything.
 *
 * FRONT-1122 follow-up: the draft (and the dialog) also cover **People**
 * (mirrors the Work detail page's "Important people" — a tick-to-include row
 * per proposed person, name + optional role, both editable) and **Context**
 * (mirrors that page's "Context" — free-text notes plus tick-to-include
 * labelled "fact" rows). Both sections use the SAME row shape as the task
 * list (checkbox + editable fields + remove button, plus a dashed "+ Add a…"
 * affordance) so the dialog reads as one family of controls, and both stay
 * fully usable by hand when the draft comes back empty — same manual-fallback
 * contract as tasks. `manualOverride` (see below) now guards ALL four
 * sections at once: `draft-loaded` seeds project/tasks/people/context
 * together, so a late-landing extract result can never clobber only SOME of
 * what the user has already typed.
 */

import { useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ClipboardText, CircleNotch, XIcon } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import { Input } from '~/components/ui/input';
import { Textarea } from '~/components/ui/textarea';
import { Checkbox } from '~/components/ui/checkbox';
import { Skeleton } from '~/components/ui/skeleton';
import { InlineMarkdown } from '~/components/inline-markdown';
import { cn } from '~/lib/utils';
import { ContainerPicker, type ContainerPickerOption } from '~/features/work/container-picker';
import { PersonNameInput } from '~/features/work/person-name-input';
import {
  useExtractPlan,
  useCommitPlan,
  resolvePlanCommitPayload,
  planSourceKey,
  DuplicateProjectError,
  MAX_COMMIT_PEOPLE,
  PERSON_NAME_MAX,
  PERSON_ROLE_MAX,
  MAX_COMMIT_CONTEXT_FACTS,
  CONTEXT_LABEL_MAX,
  CONTEXT_VALUE_MAX,
  CONTEXT_NOTES_MAX,
  type PlanDraft,
  type PlanProjectDraft,
  type PlanTaskEdit,
  type PlanPersonEdit,
  type PlanFactEdit,
  type PlanSource,
  type CommitPlanResult,
} from '~/hooks/mutations/use-plan-mutations';

export interface EditableTaskRow extends PlanTaskEdit {
  key: string;
}

/** A proposed person row as the dialog edits it — mirrors `EditableTaskRow`. */
export interface EditablePersonRow extends PlanPersonEdit {
  key: string;
}

/** A proposed context-fact row as the dialog edits it — mirrors
 *  `EditableTaskRow`. The notes text is NOT a row — it's a single string on
 *  `PlanEditorState.notes`. */
export interface EditableFactRow extends PlanFactEdit {
  key: string;
}

export interface PlanEditorState {
  project: PlanProjectDraft;
  tasks: EditableTaskRow[];
  people: EditablePersonRow[];
  facts: EditableFactRow[];
  /** The Context section's free-text summary — a single string, not a row
   *  list (see `EditableFactRow`'s docblock above). */
  notes: string;
  /** The last `PlanDraft` object (by reference) applied into this state —
   *  guards `draft-loaded` against re-seeding on every render once a draft
   *  has already landed, while still reseeding when a NEW extract result
   *  arrives (e.g. the dialog was reopened for the same session). */
  appliedDraft: PlanDraft | null;
  /** Set by the 409 duplicate-project response; cleared once the user picks
   *  "Use existing project instead" or edits the project again.
   *  FRONT-1265 code-review: `matchedVia`/`requestedName` tell apart a real
   *  active-name collision from a hit on a retired also-known-as alias — in
   *  the alias case `requestedName` (what was typed) and `name` (the
   *  existing project's actual name) are two different strings. */
  duplicateNotice: { id: string; name: string; matchedVia: 'name' | 'alias'; requestedName: string } | null;
  /**
   * FRONT-895 review fix: set once the reading state is abandoned — either
   * the ~20s client-side extract timeout fired or the user clicked "Enter
   * manually" — so the dialog drops straight to the (already-seeded, empty)
   * manual form and STOPS honoring any extract draft that lands afterward.
   * Without this, a slow extract landing after the user already started
   * typing manually would silently clobber their edits. Reset on every fresh
   * dialog open (`reset-for-open`).
   */
  manualOverride: boolean;
}

export type PlanEditorAction =
  | { type: 'draft-loaded'; draft: PlanDraft; fallbackName: string }
  | { type: 'set-project-name'; name: string }
  | { type: 'select-existing-project'; id: string; name: string }
  | { type: 'select-new-project' }
  | { type: 'toggle-task'; key: string }
  | { type: 'edit-task-title'; key: string; title: string }
  | { type: 'edit-task-due-date'; key: string; dueDate: string | null }
  | { type: 'remove-task'; key: string }
  | { type: 'add-task'; key: string }
  | { type: 'toggle-person'; key: string }
  | { type: 'edit-person-name'; key: string; name: string }
  | { type: 'edit-person-role'; key: string; role: string }
  | { type: 'remove-person'; key: string }
  | { type: 'add-person'; key: string }
  | { type: 'toggle-fact'; key: string }
  | { type: 'edit-fact-label'; key: string; label: string }
  | { type: 'edit-fact-value'; key: string; value: string }
  | { type: 'remove-fact'; key: string }
  | { type: 'add-fact'; key: string }
  | { type: 'edit-notes'; notes: string }
  | {
      type: 'duplicate-project-detected';
      existing: { id: string; name: string };
      matchedVia: 'name' | 'alias';
      requestedName: string;
    }
  | { type: 'dismiss-duplicate-notice' }
  | { type: 'force-manual' }
  | { type: 'reset-for-open'; fallbackName: string };

export function initialPlanEditorState(fallbackName: string): PlanEditorState {
  return {
    project: { mode: 'new', name: fallbackName },
    tasks: [],
    people: [],
    facts: [],
    notes: '',
    appliedDraft: null,
    duplicateNotice: null,
    manualOverride: false,
  };
}

/**
 * Pure: what the editable project + task/people/fact rows and notes text
 * should look like right after an extract draft lands. `mode:"new"` falls
 * back to `fallbackName` (the chat title) when the draft's own name is blank
 * — the server's degrade-on-failure empty draft has `name:""`, so this is
 * what keeps the manual-fallback case from opening on a blank field with
 * nothing to anchor a name on.
 */
export function deriveEditableStateFromDraft(
  draft: PlanDraft,
  fallbackName: string,
): {
  project: PlanProjectDraft;
  tasks: EditableTaskRow[];
  people: EditablePersonRow[];
  facts: EditableFactRow[];
  notes: string;
} {
  const project: PlanProjectDraft =
    draft.project.mode === 'existing'
      ? { mode: 'existing', entityId: draft.project.entityId, name: draft.project.name ?? '' }
      : { mode: 'new', name: draft.project.name.trim() || fallbackName };

  const tasks: EditableTaskRow[] = draft.tasks.map((t, i) => ({
    key: `extract-${i}`,
    title: t.title,
    dueDate: t.dueDate,
    // FRONT-2423: carried unchanged — this dialog never edits it, only
    // shows it (see the task row's own description line below).
    description: t.description ?? null,
    included: true,
  }));

  const people: EditablePersonRow[] = draft.people.map((p, i) => ({
    key: `person-${i}`,
    name: p.name,
    role: p.role ?? '',
    included: true,
  }));

  const facts: EditableFactRow[] = draft.context.facts.map((f, i) => ({
    key: `fact-${i}`,
    label: f.label,
    value: f.value,
    included: true,
  }));

  return { project, tasks, people, facts, notes: draft.context.notes };
}

export function planEditorReducer(state: PlanEditorState, action: PlanEditorAction): PlanEditorState {
  switch (action.type) {
    case 'draft-loaded': {
      if (state.appliedDraft === action.draft) return state;
      const seeded = deriveEditableStateFromDraft(action.draft, action.fallbackName);
      return { ...state, ...seeded, appliedDraft: action.draft, duplicateNotice: null };
    }
    case 'set-project-name':
      return { ...state, project: { mode: 'new', name: action.name }, duplicateNotice: null };
    case 'select-existing-project':
      return {
        ...state,
        project: { mode: 'existing', entityId: action.id, name: action.name },
        duplicateNotice: null,
      };
    case 'select-new-project':
      return { ...state, project: { mode: 'new', name: '' }, duplicateNotice: null };
    case 'toggle-task':
      return {
        ...state,
        tasks: state.tasks.map((t) => (t.key === action.key ? { ...t, included: !t.included } : t)),
      };
    case 'edit-task-title':
      return {
        ...state,
        tasks: state.tasks.map((t) => (t.key === action.key ? { ...t, title: action.title } : t)),
      };
    case 'edit-task-due-date':
      return {
        ...state,
        tasks: state.tasks.map((t) => (t.key === action.key ? { ...t, dueDate: action.dueDate } : t)),
      };
    case 'remove-task':
      return { ...state, tasks: state.tasks.filter((t) => t.key !== action.key) };
    case 'add-task':
      return {
        ...state,
        tasks: [...state.tasks, { key: action.key, title: '', dueDate: null, description: null, included: true }],
      };
    case 'toggle-person':
      return {
        ...state,
        people: state.people.map((p) => (p.key === action.key ? { ...p, included: !p.included } : p)),
      };
    case 'edit-person-name':
      return {
        ...state,
        people: state.people.map((p) => (p.key === action.key ? { ...p, name: action.name } : p)),
      };
    case 'edit-person-role':
      return {
        ...state,
        people: state.people.map((p) => (p.key === action.key ? { ...p, role: action.role } : p)),
      };
    case 'remove-person':
      return { ...state, people: state.people.filter((p) => p.key !== action.key) };
    case 'add-person':
      return { ...state, people: [...state.people, { key: action.key, name: '', role: '', included: true }] };
    case 'toggle-fact':
      return {
        ...state,
        facts: state.facts.map((f) => (f.key === action.key ? { ...f, included: !f.included } : f)),
      };
    case 'edit-fact-label':
      return {
        ...state,
        facts: state.facts.map((f) => (f.key === action.key ? { ...f, label: action.label } : f)),
      };
    case 'edit-fact-value':
      return {
        ...state,
        facts: state.facts.map((f) => (f.key === action.key ? { ...f, value: action.value } : f)),
      };
    case 'remove-fact':
      return { ...state, facts: state.facts.filter((f) => f.key !== action.key) };
    case 'add-fact':
      return { ...state, facts: [...state.facts, { key: action.key, label: '', value: '', included: true }] };
    case 'edit-notes':
      return { ...state, notes: action.notes };
    case 'duplicate-project-detected':
      return {
        ...state,
        duplicateNotice: {
          ...action.existing,
          matchedVia: action.matchedVia,
          requestedName: action.requestedName,
        },
      };
    case 'dismiss-duplicate-notice':
      return { ...state, duplicateNotice: null };
    case 'force-manual':
      // Stop reading and hand the user the form as it currently stands. On a
      // FIRST open that is the initial fallback-seeded, empty shape; a reopen
      // is made equivalent by `reset-for-open` wiping the state below, so the
      // user can never be handed the previous run's rows as a "manual" form.
      if (state.manualOverride) return state;
      return { ...state, manualOverride: true };
    case 'reset-for-open':
      // A full reset, not just the flag. This dialog instance is reused
      // across opens, so the previous run's project name, task/people/fact
      // rows, notes, duplicate notice and applied-draft marker all survive
      // unless they are cleared here — and a reopen that then goes manual
      // (timeout or "Enter manually") would otherwise present the FIRST
      // conversation's rows as a fresh form, and create them on confirm.
      return initialPlanEditorState(action.fallbackName);
    default:
      return state;
  }
}

/** "4 of 5 selected" when some are excluded; "1 task" / "N tasks" when every
 *  row is included. */
export function describeTaskSelection(total: number, included: number): string {
  if (total === 0) return '0 tasks';
  if (included === total) return total === 1 ? '1 task' : `${total} tasks`;
  return `${included} of ${total} selected`;
}

/**
 * FRONT-895 review fix: an included row with a blank/whitespace-only title
 * used to still count toward the footer's task total — showing an enabled
 * "Create project & 1 task" button that then committed `tasks:[]` (a new
 * EMPTY project), because `resolvePlanCommitPayload` drops exactly that row.
 * Count only included rows whose TRIMMED title is non-empty — the same rule
 * `resolvePlanCommitPayload` applies — so the label and the disabled state
 * can never promise a task the commit won't actually create.
 */
export function countCommittableTasks(tasks: EditableTaskRow[]): number {
  return tasks.filter((t) => t.included && t.title.trim().length > 0).length;
}

/** Same rule as `countCommittableTasks`, for the People section: only an
 *  included row whose TRIMMED name is non-empty is content the commit will
 *  actually send (mirrors `resolvePlanCommitPayload`'s own filter). */
export function countCommittablePeople(people: EditablePersonRow[]): number {
  return people.filter((p) => p.included && p.name.trim().length > 0).length;
}

/** Same rule, for the Context section's fact rows — an included row needs a
 *  non-empty TRIMMED label to be sent. */
export function countCommittableFacts(facts: EditableFactRow[]): number {
  return facts.filter((f) => f.included && f.label.trim().length > 0).length;
}

/** Plain-English "a, b and c" join — used by both the confirm button's label
 *  and the success/warning toast so a multi-part summary reads as one
 *  sentence fragment rather than a bare comma list. */
function joinWithAnd(parts: string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * The dialog's own heading. It follows the same mode switch as the confirm
 * button below it: when the draft resolves to a container that already exists
 * — a designed, common path — nothing is created, and a header reading
 * "Create project" over a button reading "Add 3 tasks" tells the reader two
 * different stories (FRONT-1141 code-review finding). The old "Create plan"
 * was vague enough to cover both; "Create project" is not.
 */
export function describeDialogTitle(mode: 'new' | 'existing'): string {
  return mode === 'new' ? 'Create project' : 'Add to project';
}

/**
 * The primary button's label — "Create project & N tasks" for a new
 * project, "Add task" / "Add N tasks" for an existing one. `extras` (added by
 * the FRONT-1122 follow-up) folds in the committable people count and whether
 * the Context section has anything to send, so the button keeps honestly
 * describing everything it's about to do rather than only the tasks. Calling
 * this with just `(mode, taskCount)` — every pre-existing call site — is
 * BYTE-IDENTICAL to the original behavior; the extended, multi-part phrasing
 * only kicks in once there's something beyond tasks to mention.
 */
export function describeCommitLabel(
  mode: 'new' | 'existing',
  taskCount: number,
  extras: { people?: number; context?: boolean } = {},
): string {
  const people = extras.people ?? 0;
  const includesContext = extras.context ?? false;
  if (people === 0 && !includesContext) {
    if (mode === 'new') return `Create project & ${taskCount} ${taskCount === 1 ? 'task' : 'tasks'}`;
    return taskCount === 1 ? 'Add task' : `Add ${taskCount} tasks`;
  }

  const parts: string[] = [];
  if (taskCount > 0) parts.push(`${taskCount} ${taskCount === 1 ? 'task' : 'tasks'}`);
  if (people > 0) parts.push(`${people} ${people === 1 ? 'person' : 'people'}`);
  if (includesContext) parts.push('context');
  const summary = joinWithAnd(parts);
  return mode === 'new' ? `Create project & ${summary}` : `Add ${summary}`;
}

/** "4 of 5 selected" when some are excluded; "1 person" / "N people" when
 *  every row is included — mirrors `describeTaskSelection`. */
export function describePeopleSelection(total: number, included: number): string {
  if (total === 0) return '0 people';
  if (included === total) return total === 1 ? '1 person' : `${total} people`;
  return `${included} of ${total} selected`;
}

/** Mirrors `describeTaskSelection` for the Context section's fact rows. */
export function describeFactSelection(total: number, included: number): string {
  if (total === 0) return '0 details';
  if (included === total) return total === 1 ? '1 detail' : `${total} details`;
  return `${included} of ${total} selected`;
}

/**
 * FRONT-1122 defect fix: shown in place of the "+ Add a…" affordance once
 * the People/Context row count hits the server's own cap
 * (`MAX_COMMIT_PEOPLE`/`MAX_COMMIT_CONTEXT_FACTS`, `use-plan-mutations.ts`),
 * so a limit that blocks the user is explained in plain words rather than
 * the row just silently disappearing. Mirrors `capReachedMessage` in
 * `src/work/schedule-service.ts`.
 */
export function describeRowLimitReached(noun: 'people' | 'details'): string {
  const max = noun === 'people' ? MAX_COMMIT_PEOPLE : MAX_COMMIT_CONTEXT_FACTS;
  return `This already has the maximum of ${max} ${noun} — remove one to add another.`;
}

export interface CommitOutcomeSummary {
  /** `warning` (amber) — never `error`/destructive red, because the project
   *  itself still landed; only part of what came with it didn't. Same
   *  precedent as `resolveEntitySaveOutcome` in `~/lib/entity-save-outcome.ts`. */
  toastVariant: 'success' | 'warning';
  message: string;
}

/**
 * Pure: what the commit's success/warning toast should say, given the
 * server's response. Built from what LANDED (`createdCount`, the `ok`
 * people, a `context` that reports `ok` with something actually written),
 * not from what the dialog SENT — so a person or the context that the
 * server reports as failed is named plainly instead of folded into a
 * claimed-complete "Created…" message. `people` and `context` are always
 * present on the response (FRONT-1122 defect fix — there is no longer a
 * task-only response shape to special-case; an empty `people` array and an
 * all-zero `context` object read exactly like the original task-only
 * wording).
 *
 * Five more cases are the never-clobber/cap rules working AS DESIGNED, not
 * failures the server reports as `ok: false` — but the user still must be
 * told, because a silent skip reads as false success (a ticked "Budget:
 * 25k" that quietly kept the old "10k" is worse than no ticket at all):
 * `factsSkippedExisting` (a ticked detail wasn't written because that label
 * is already on the project), `factsSkippedLimit` (the project's 50-detail
 * cap is reached), `notesSkipped:"duplicate"` (the notes were already
 * there) or `notesSkipped:"limit"` (appending would exceed the 4000-char
 * cap), and `roleSkipped` on a person (already on the project under a
 * different role, so the typed role was left alone). Each gets one short,
 * plain-English clause in the same warning (never destructive-red) style as
 * the existing partial-failure clauses below.
 */
export function describeCommitOutcome(result: CommitPlanResult): CommitOutcomeSummary {
  const taskCount = result.createdCount;
  const people = result.people;
  const peopleOk = people.filter((p) => p.ok).length;
  const peopleFailed = people.length - peopleOk;
  const context = result.context;
  const contextLanded = context.ok && (context.factsWritten > 0 || context.notesWritten);
  const contextFailed = !context.ok;
  const projectName = result.project.name;

  const parts: string[] = [];
  if (taskCount > 0) parts.push(`${taskCount} ${taskCount === 1 ? 'task' : 'tasks'}`);
  if (peopleOk > 0) parts.push(`${peopleOk} ${peopleOk === 1 ? 'person' : 'people'}`);
  if (contextLanded) parts.push('context');
  const summary = joinWithAnd(parts);

  let base: string;
  if (!result.project.existed) {
    base = summary ? `Created "${projectName}" with ${summary}` : `Created "${projectName}"`;
  } else if (peopleOk === 0 && !contextLanded && taskCount === 1) {
    // Task-only, singular, added to an existing project — preserves the
    // original wording exactly ("Task added", never "1 task added").
    base = `Task added to "${projectName}"`;
  } else {
    base = summary ? `${summary} added to "${projectName}"` : `"${projectName}" is already up to date`;
    base = base.charAt(0).toUpperCase() + base.slice(1);
  }

  const failures: string[] = [];
  if (peopleFailed > 0) {
    failures.push(`${peopleFailed} ${peopleFailed === 1 ? "person didn't" : "people didn't"} get added`);
  }
  if (contextFailed) failures.push("the context didn't save");

  if (context.factsSkippedExisting > 0) {
    failures.push(
      context.factsSkippedExisting === 1
        ? "1 detail wasn't changed since it's already set"
        : `${context.factsSkippedExisting} details weren't changed since they're already set`,
    );
  }
  if (context.factsSkippedLimit > 0) {
    failures.push(
      context.factsSkippedLimit === 1
        ? "1 detail wasn't added since the project's already full"
        : `${context.factsSkippedLimit} details weren't added since the project's already full`,
    );
  }
  if (context.notesSkipped === 'duplicate') failures.push('the notes were already there');
  if (context.notesSkipped === 'limit') failures.push('the notes were too long to add');

  const roleSkippedCount = people.filter((p) => p.roleSkipped).length;
  if (roleSkippedCount > 0) {
    failures.push(
      roleSkippedCount === 1
        ? "1 person's role was left as is since they're already on the project"
        : `${roleSkippedCount} people's roles were left as is since they're already on the project`,
    );
  }

  if (failures.length === 0) return { toastVariant: 'success', message: base };
  return { toastVariant: 'warning', message: `${base} — but ${joinWithAnd(failures)}.` };
}

/** The word used in the reading indicator ("Eddy is reading this …") and the
 *  "From this …:" line — one noun per `PlanSource` kind (FRONT-1122). */
export function describeSourceNoun(kind: PlanSource['kind']): string {
  switch (kind) {
    case 'chat':
      return 'chat';
    case 'file':
      return 'file';
    case 'document':
      return 'document';
    case 'cron-run':
      return 'briefing';
    case 'text':
      return 'text';
  }
}

/** Fallback text for the "From this …:" line when no label is available —
 *  mirrors the original chat-only "this conversation" fallback, one per kind. */
export function describeSourceFallback(kind: PlanSource['kind']): string {
  switch (kind) {
    case 'chat':
      return 'this conversation';
    case 'file':
      return 'this file';
    case 'document':
      return 'this document';
    case 'cron-run':
      return 'this briefing';
    case 'text':
      return 'this text';
  }
}

export interface CreatePlanDialogProps {
  source: PlanSource;
  /** A human label for the source — the chat's title, the file's name, the
   *  document's title, the briefing's job name. Falls back per-kind (see
   *  `describeSourceFallback`) when blank/absent. */
  sourceLabel?: string | null;
  /** Whether the success toast offers a jump to the new project's page.
   *  Defaults to true — every desktop entry point wants it. The phone surface
   *  (FRONT-1317) passes false: it has no project view of its own, and the
   *  desktop Work page it would land on is a fixed multi-column layout with no
   *  route back to the phone shell. */
  offerOpenProject?: boolean;
  /** FRONT-1696: fired once a commit actually lands, with the server's full
   *  result — additive and optional, so every existing call site is
   *  unaffected. The "Import a Claude Project" flow uses this to run its own
   *  best-effort follow-ups (attach uploaded knowledge files, save the
   *  pasted text as the new project's standalone custom instructions) once
   *  the real project id is known, without this dialog needing to know
   *  anything about either. Fired BEFORE the success toast/navigate below,
   *  so a caller's own toast (if any) doesn't get buried under this one. */
  onCommitSuccess?: (result: CommitPlanResult) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** FRONT-895 review fix: the backend allows up to 120s for the extract LLM
 *  pass, but the dialog promises "works fully manually if extract fails" —
 *  that promise is empty if the whole form stays hidden behind a spinner for
 *  two minutes. After this long without a landed draft, drop to the empty
 *  manual form on our own (the in-flight extract is left to finish in the
 *  background and simply ignored if it lands after this point). */
const EXTRACT_READ_TIMEOUT_MS = 20_000;

export function CreatePlanDialog({
  source,
  sourceLabel,
  offerOpenProject = true,
  onCommitSuccess,
  open,
  onOpenChange,
}: CreatePlanDialogProps) {
  const navigate = useNavigate();
  const fallbackName = sourceLabel?.trim() || 'New project';
  const sourceNoun = describeSourceNoun(source.kind);
  const sourceKey = planSourceKey(source);
  const [state, dispatch] = useReducer(planEditorReducer, fallbackName, initialPlanEditorState);
  const extractPlan = useExtractPlan();
  const commitPlan = useCommitPlan();
  const taskCounterRef = useRef(0);
  const personCounterRef = useRef(0);
  const factCounterRef = useRef(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const manualTimeoutRef = useRef<number | null>(null);

  function clearManualTimeout() {
    if (manualTimeoutRef.current != null) {
      window.clearTimeout(manualTimeoutRef.current);
      manualTimeoutRef.current = null;
    }
  }

  // Fire the read-only extract pass as soon as the dialog opens, and start
  // the client-side "give up on reading" timeout alongside it. Guarded on
  // [open, sourceKey] only — re-fires on every open (including reopening for
  // the same source), never on unrelated re-renders. `sourceKey` (a primitive
  // string) is used instead of `source` itself because call sites build
  // `source` as a fresh inline object literal on every render — an object in
  // the dependency array would re-fire the extract pass on any parent
  // re-render, not just an actual source change. `reset-for-open` clears any
  // `manualOverride` left over from a previous open of this same dialog
  // instance so a fresh open always gets a fresh reading attempt.
  useEffect(() => {
    if (!open) return;
    dispatch({ type: 'reset-for-open', fallbackName });
    extractPlan.mutate(source);
    manualTimeoutRef.current = window.setTimeout(() => {
      dispatch({ type: 'force-manual' });
    }, EXTRACT_READ_TIMEOUT_MS);
    return () => clearManualTimeout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceKey]);

  // Clean up the timeout the moment extract actually resolves (success or
  // its own error fallback) — no reason to keep a "force manual" timer
  // ticking once reading is already over.
  useEffect(() => {
    if (!extractPlan.isPending) clearManualTimeout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractPlan.isPending]);

  /** The "Enter manually" escape hatch: cancels the pending timeout and
   *  drops straight to the manual form, ignoring whatever the in-flight
   *  extract call eventually returns. */
  function dropToManual() {
    clearManualTimeout();
    dispatch({ type: 'force-manual' });
  }

  // Seed the editable state the moment a draft lands. Calling `dispatch`
  // during render (guarded by the `appliedDraft` reference check inside the
  // reducer) is the "adjust state when a prop/query result changes" pattern —
  // safe because it's idempotent per draft object, and it's what lets a
  // single static render (this repo's test convention, see
  // new-work-entity-dialog.test.tsx) show the seeded rows without needing an
  // effect to actually flush.
  // Once `manualOverride` is set (timeout fired or "Enter manually" was
  // clicked), stop honoring a landed draft — the user may already be typing
  // manually and a late-arriving extract result must not clobber that.
  const incomingDraft = extractPlan.data?.draft;
  if (incomingDraft && incomingDraft !== state.appliedDraft && !state.manualOverride) {
    dispatch({ type: 'draft-loaded', draft: incomingDraft, fallbackName });
  }

  const reading = extractPlan.isPending && !state.manualOverride;
  const committing = commitPlan.isPending;
  const includedCount = state.tasks.filter((t) => t.included).length;
  const committableTaskCount = countCommittableTasks(state.tasks);
  const includedPeopleCount = state.people.filter((p) => p.included).length;
  const committablePeopleCount = countCommittablePeople(state.people);
  const includedFactCount = state.facts.filter((f) => f.included).length;
  const committableFactCount = countCommittableFacts(state.facts);
  const notesHaveContent = state.notes.trim().length > 0;
  const includesContext = committableFactCount > 0 || notesHaveContent;
  // Never promise content the commit won't actually create — same rule
  // `countCommittableTasks`'s docblock explains, now spanning all four
  // sections: a click on "Create project" must always send SOMETHING.
  const hasCommittableContent =
    committableTaskCount > 0 || committablePeopleCount > 0 || includesContext;

  function addTask() {
    taskCounterRef.current += 1;
    dispatch({ type: 'add-task', key: `manual-${taskCounterRef.current}` });
  }

  function addPerson() {
    personCounterRef.current += 1;
    dispatch({ type: 'add-person', key: `person-manual-${personCounterRef.current}` });
  }

  function addFact() {
    factCounterRef.current += 1;
    dispatch({ type: 'add-fact', key: `fact-manual-${factCounterRef.current}` });
  }

  function runCommit(project: PlanProjectDraft) {
    const payload = resolvePlanCommitPayload(project, state.tasks, state.people, state.facts, state.notes);
    commitPlan.mutate(
      { source, ...payload },
      {
        onSuccess: (result) => {
          onCommitSuccess?.(result);
          const outcome = describeCommitOutcome(result);
          const fireToast = outcome.toastVariant === 'warning' ? toast.warning : toast.success;
          fireToast(outcome.message, {
            action: offerOpenProject
              ? {
                  label: 'Open project',
                  onClick: () => navigate(`/work/${result.project.id}`),
                }
              : undefined,
          });
          onOpenChange(false);
        },
        onError: (err) => {
          if (err instanceof DuplicateProjectError) {
            dispatch({
              type: 'duplicate-project-detected',
              existing: err.existing,
              matchedVia: err.matchedVia,
              requestedName: err.requestedName,
            });
          } else {
            toast.error(err instanceof Error ? err.message : 'Could not create the project');
          }
        },
      },
    );
  }

  function handleCommit() {
    runCommit(state.project);
  }

  function useExistingInstead() {
    const existing = state.duplicateNotice;
    if (!existing) return;
    dispatch({ type: 'select-existing-project', id: existing.id, name: existing.name });
    runCommit({ mode: 'existing', entityId: existing.id, name: existing.name });
  }

  function handleSelectExisting(option: ContainerPickerOption) {
    dispatch({ type: 'select-existing-project', id: option.id, name: option.name });
  }

  function handleSelectNew() {
    dispatch({ type: 'select-new-project' });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* FRONT-1122 follow-up: the dialog grew a People and a Context section
          on top of Project/Tasks, so it's capped at 85dvh (dvh, not vh — see
          FRONT-1255's cron-form fix) as a three-part flex column: pinned
          header, an internally scrolling form body, pinned footer — the same
          shape FRONT-920 established for the plan card so a long draft can
          never push Confirm/Cancel off-screen again. */}
      <DialogContent className="flex max-h-[85dvh] flex-col overflow-hidden sm:max-w-md">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ClipboardText size={18} weight="regular" /> {describeDialogTitle(state.project.mode)}
          </DialogTitle>
          <DialogDescription>
            From this {sourceNoun}: {sourceLabel?.trim() || describeSourceFallback(source.kind)}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {reading ? (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-2">
                  <CircleNotch size={14} className="animate-spin" /> EDDY is reading this {sourceNoun}…
                </span>
                <button
                  type="button"
                  onClick={dropToManual}
                  className="shrink-0 underline-offset-2 hover:text-foreground hover:underline"
                >
                  Enter manually
                </button>
              </div>
              <Skeleton className="h-8 w-2/3" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-3/4" />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="space-y-1.5">
                <div className="text-xs font-medium text-muted-foreground">Project</div>
                {state.project.mode === 'new' ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={state.project.name}
                      onChange={(e) => dispatch({ type: 'set-project-name', name: e.target.value })}
                      placeholder="Project name"
                      aria-label="Project name"
                      disabled={committing}
                      className="flex-1"
                    />
                    <ContainerPicker
                      open={pickerOpen}
                      onOpenChange={setPickerOpen}
                      trigger={
                        <Button type="button" variant="outline" size="sm" disabled={committing}>
                          Pick existing
                        </Button>
                      }
                      onSelectExisting={handleSelectExisting}
                      onSelectNew={handleSelectNew}
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="max-w-[70%] truncate">
                      {state.project.name}
                    </Badge>
                    <ContainerPicker
                      open={pickerOpen}
                      onOpenChange={setPickerOpen}
                      value={state.project.entityId}
                      trigger={
                        <Button type="button" variant="ghost" size="sm" disabled={committing}>
                          Change
                        </Button>
                      }
                      onSelectExisting={handleSelectExisting}
                      onSelectNew={handleSelectNew}
                    />
                  </div>
                )}
                {state.duplicateNotice && (
                  <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/50 p-2 text-xs text-muted-foreground">
                    {/* FRONT-1265 code-review: when this hit came from an
                        also-known-as alias a past merge left behind, the
                        typed name and the existing project's actual name are
                        two different strings — name both instead of implying
                        the user's own input already exists verbatim. */}
                    <span>
                      {state.duplicateNotice.matchedVia === 'alias' ? (
                        <>
                          "{state.duplicateNotice.requestedName}" is an also-known-as name for "
                          {state.duplicateNotice.name}" — that's an existing project.
                        </>
                      ) : (
                        <>A project named "{state.duplicateNotice.name}" already exists.</>
                      )}
                    </span>
                    <Button type="button" size="xs" variant="secondary" onClick={useExistingInstead}>
                      Use existing project instead
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
                  <span>Tasks</span>
                  <span>{describeTaskSelection(state.tasks.length, includedCount)}</span>
                </div>
                <ul className="flex flex-col gap-1">
                  {state.tasks.map((task) => (
                    <li key={task.key} className="group flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={task.included}
                          onCheckedChange={() => dispatch({ type: 'toggle-task', key: task.key })}
                          disabled={committing}
                          aria-label={task.included ? `Exclude "${task.title}"` : `Include "${task.title}"`}
                        />
                        <Input
                          value={task.title}
                          onChange={(e) =>
                            dispatch({ type: 'edit-task-title', key: task.key, title: e.target.value })
                          }
                          placeholder="Task title"
                          aria-label="Task title"
                          disabled={committing}
                          className={cn(
                            'h-8 flex-1 text-sm',
                            !task.included && 'text-muted-foreground line-through opacity-60',
                          )}
                        />
                        <input
                          type="date"
                          value={task.dueDate ?? ''}
                          onChange={(e) =>
                            dispatch({
                              type: 'edit-task-due-date',
                              key: task.key,
                              dueDate: e.target.value || null,
                            })
                          }
                          disabled={committing}
                          aria-label="Due date"
                          className="h-8 shrink-0 rounded-md border border-input bg-transparent px-1.5 text-xs text-muted-foreground outline-none focus-visible:border-ring disabled:opacity-50"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          className="shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() => dispatch({ type: 'remove-task', key: task.key })}
                          disabled={committing}
                          aria-label={`Remove "${task.title || 'task'}"`}
                        >
                          <XIcon size={12} />
                        </Button>
                      </div>
                      {/* FRONT-2423: the extract pass's own detail for this
                          task, shown but never edited here — a muted,
                          line-clamped subtitle under the row, same treatment
                          as every other short description in the app
                          (`InlineMarkdown`, never a raw string). */}
                      {task.description && (
                        <InlineMarkdown
                          text={task.description}
                          className="line-clamp-2 pl-6 text-xs text-muted-foreground"
                        />
                      )}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={addTask}
                  disabled={committing}
                  className="rounded-md border border-dashed border-border px-2.5 py-1.5 text-left text-xs text-muted-foreground hover:bg-muted/40 disabled:opacity-50"
                >
                  + Add a task
                </button>
              </div>

              {/* FRONT-1122 follow-up: People — mirrors the task list's row
                  shape exactly (checkbox + editable fields + remove button)
                  so the two read as one family. Stays fully usable with zero
                  rows (the manual-fallback contract every section shares). */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
                  <span>People</span>
                  <span>{describePeopleSelection(state.people.length, includedPeopleCount)}</span>
                </div>
                <ul className="flex flex-col gap-1">
                  {state.people.map((person) => (
                    <li key={person.key} className="group flex items-center gap-2">
                      <Checkbox
                        checked={person.included}
                        onCheckedChange={() => dispatch({ type: 'toggle-person', key: person.key })}
                        disabled={committing}
                        aria-label={
                          person.included
                            ? `Exclude ${person.name || 'this person'}`
                            : `Include ${person.name || 'this person'}`
                        }
                      />
                      <PersonNameInput
                        value={person.name}
                        onChange={(name) => dispatch({ type: 'edit-person-name', key: person.key, name })}
                        label="Person name"
                        placeholder="Name"
                        disabled={committing}
                        maxLength={PERSON_NAME_MAX}
                        className={cn(
                          'h-8 flex-1 text-sm',
                          !person.included && 'text-muted-foreground line-through opacity-60',
                        )}
                      />
                      <Input
                        value={person.role}
                        onChange={(e) =>
                          dispatch({ type: 'edit-person-role', key: person.key, role: e.target.value })
                        }
                        placeholder="Role (optional)"
                        aria-label="Person role"
                        disabled={committing}
                        maxLength={PERSON_ROLE_MAX}
                        className="h-8 w-32 shrink-0 text-xs"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => dispatch({ type: 'remove-person', key: person.key })}
                        disabled={committing}
                        aria-label={`Remove ${person.name || 'person'}`}
                      >
                        <XIcon size={12} />
                      </Button>
                    </li>
                  ))}
                </ul>
                {state.people.length >= MAX_COMMIT_PEOPLE ? (
                  <p className="px-2.5 py-1.5 text-xs text-muted-foreground">{describeRowLimitReached('people')}</p>
                ) : (
                  <button
                    type="button"
                    onClick={addPerson}
                    disabled={committing}
                    className="rounded-md border border-dashed border-border px-2.5 py-1.5 text-left text-xs text-muted-foreground hover:bg-muted/40 disabled:opacity-50"
                  >
                    + Add a person
                  </button>
                )}
              </div>

              {/* FRONT-1122 follow-up: Context — mirrors the Work detail
                  page's Context panel (facts, then notes), but with the same
                  tick-to-include row shape the task/people lists use, so a
                  proposed detail can be excluded before it's ever saved. */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
                  <span>Context</span>
                  <span>{describeFactSelection(state.facts.length, includedFactCount)}</span>
                </div>
                <ul className="flex flex-col gap-1">
                  {state.facts.map((fact) => (
                    <li key={fact.key} className="group flex items-center gap-2">
                      <Checkbox
                        checked={fact.included}
                        onCheckedChange={() => dispatch({ type: 'toggle-fact', key: fact.key })}
                        disabled={committing}
                        aria-label={
                          fact.included
                            ? `Exclude ${fact.label || 'this detail'}`
                            : `Include ${fact.label || 'this detail'}`
                        }
                      />
                      <Input
                        value={fact.label}
                        onChange={(e) =>
                          dispatch({ type: 'edit-fact-label', key: fact.key, label: e.target.value })
                        }
                        placeholder="Label"
                        aria-label="Detail label"
                        disabled={committing}
                        maxLength={CONTEXT_LABEL_MAX}
                        className="h-8 w-32 shrink-0 text-xs"
                      />
                      <Input
                        value={fact.value}
                        onChange={(e) =>
                          dispatch({ type: 'edit-fact-value', key: fact.key, value: e.target.value })
                        }
                        placeholder="Value"
                        aria-label="Detail value"
                        disabled={committing}
                        maxLength={CONTEXT_VALUE_MAX}
                        className={cn(
                          'h-8 flex-1 text-sm',
                          !fact.included && 'text-muted-foreground line-through opacity-60',
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => dispatch({ type: 'remove-fact', key: fact.key })}
                        disabled={committing}
                        aria-label={`Remove ${fact.label || 'detail'}`}
                      >
                        <XIcon size={12} />
                      </Button>
                    </li>
                  ))}
                </ul>
                {state.facts.length >= MAX_COMMIT_CONTEXT_FACTS ? (
                  <p className="px-2.5 py-1.5 text-xs text-muted-foreground">{describeRowLimitReached('details')}</p>
                ) : (
                  <button
                    type="button"
                    onClick={addFact}
                    disabled={committing}
                    className="rounded-md border border-dashed border-border px-2.5 py-1.5 text-left text-xs text-muted-foreground hover:bg-muted/40 disabled:opacity-50"
                  >
                    + Add a detail
                  </button>
                )}
                <div className="space-y-1 pt-1">
                  <div className="text-[length:var(--text-2xs)] text-muted-foreground">Notes</div>
                  <Textarea
                    value={state.notes}
                    onChange={(e) => dispatch({ type: 'edit-notes', notes: e.target.value })}
                    placeholder="Free-form notes about this project…"
                    aria-label="Notes"
                    disabled={committing}
                    maxLength={CONTEXT_NOTES_MAX}
                    rows={3}
                    className="text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={committing}>
            Cancel
          </Button>
          <Button type="button" onClick={handleCommit} disabled={committing || !hasCommittableContent}>
            {committing && <CircleNotch size={14} className="animate-spin" />}
            {describeCommitLabel(state.project.mode, committableTaskCount, {
              people: committablePeopleCount,
              context: includesContext,
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
